import { curSym } from "@/lib/company";
import { DatePickerTR } from "@/components/DatePickerTR";
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { fmtTL } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { DoorClosed, Eye, ShieldCheck, User, Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSort } from "@/lib/sort";
import { SortTh } from "@/components/SortTh";
import { Pagination } from "@/components/Pagination";
import { usePaged } from "@/lib/usePaged";

const dt = (iso) => (iso ? new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const tm = (iso) => (iso ? new Date(iso).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }) : "—");
const shiftPreset = (p) => {
  const end = new Date(); const start = new Date();
  if (p === "1d") start.setDate(end.getDate() - 1);
  else if (p === "1w") start.setDate(end.getDate() - 7);
  else if (p === "1m") start.setMonth(end.getMonth() - 1);
  else start.setDate(end.getDate() - 3);
  return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
};

const Row = ({ label, value, strong, accent }) => (
  <div className={`flex items-center justify-between py-1.5 text-sm ${strong ? "border-t border-slate-200 pt-2 font-semibold" : ""}`}>
    <span className="text-slate-500">{label}</span>
    <span className={`tabular ${accent || "text-slate-800"} ${strong ? "font-bold" : ""}`}>{value}</span>
  </div>
);

export default function Shifts() {
  const { isPrivileged, user, can } = useAuth();
  const myLocs = user?.locations || [];
  const [shifts, setShifts] = useState([]);
  const { sortKey, sortDir, toggle: onSort, sortRows } = useSort();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [range, setRange] = useState({ start: "", end: "" });
  const [locId, setLocId] = useState("");
  const [locations, setLocations] = useState([]);
  const locOpts = isPrivileged ? locations : myLocs;
  const showLocFilter = isPrivileged || myLocs.length > 1;

  useEffect(() => { if (isPrivileged) api.get("/locations").then((r) => setLocations(r.data)).catch(() => {}); }, [isPrivileged]);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (range.start) params.start = `${range.start}T00:00:00`;
    if (range.end) params.end = `${range.end}T23:59:59.999999`;
    if (locId) params.location_id = locId;
    api.get("/shifts", { params }).then((r) => setShifts(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [range, locId]);
  useEffect(() => { load(); }, [load]);

  const canSetThr = ["admin", "assistant"].includes(user?.role);
  const [thr, setThr] = useState("");
  const [view, setView] = useState("shifts");
  const canCloseAny = can ? can("shift.close.approve") : ["admin", "assistant", "store_manager"].includes(user?.role);
  const [openShifts, setOpenShifts] = useState([]);
  const [openLoading, setOpenLoading] = useState(false);
  const [closeTarget, setCloseTarget] = useState(null);
  const [counted, setCounted] = useState("0");
  const [adminPin, setAdminPin] = useState("");
  const [closing, setClosing] = useState(false);
  const loadOpen = useCallback(() => {
    setOpenLoading(true);
    api.get("/shifts/open").then((r) => setOpenShifts(r.data || [])).catch(() => {}).finally(() => setOpenLoading(false));
  }, []);
  useEffect(() => { if (view === "open") loadOpen(); }, [view, loadOpen]);
  const submitAdminClose = async () => {
    if (!closeTarget) return;
    if (counted === "" || isNaN(parseFloat(counted))) { toast.error("Sayılan nakit girin"); return; }
    setClosing(true);
    try {
      await api.post(`/shifts/${closeTarget.id}/admin-close`, { counted_cash: parseFloat(counted), pin: adminPin || null });
      toast.success(`Kasa kapatıldı — Z raporu alındı (${closeTarget.cashier_name})`);
      setCloseTarget(null); setCounted("0"); setAdminPin("");
      loadOpen(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Kasa kapatılamadı"); } finally { setClosing(false); }
  };
  const [emailThr, setEmailThr] = useState(false);
  useEffect(() => { api.get("/settings/cash-threshold").then((r) => { setThr(String(r.data.threshold)); setEmailThr(!!r.data.email); }).catch(() => {}); }, []);
  const saveThr = async () => {
    try { const { data } = await api.put("/settings/cash-threshold", { threshold: parseFloat(String(thr).replace(",", ".")) || 0, email: emailThr }); setThr(String(data.threshold)); setEmailThr(!!data.email); toast.success("Uyarı eşiği güncellendi"); }
    catch (e) { toast.error("Eşik kaydedilemedi (yetki?)"); }
  };

  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = {};
      if (range.start) params.start = `${range.start}T00:00:00`;
      if (range.end) params.end = `${range.end}T23:59:59.999999`;
      if (locId) params.location_id = locId;
      const res = await api.get("/shifts/cash-diff-report/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "kasa-farklari.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("CSV indirilemedi"); }
    finally { setExporting(false); }
  };

  const openDetail = async (s) => {
    setDetail({ shift: s, report: s.report, sales: null });
    try { const { data } = await api.get(`/shifts/${s.id}`); setDetail(data); }
    catch { setDetail({ shift: s, report: s.report || {}, sales: [] }); }
  };

  const r = detail?.report || {};

  const shiftAccessors = {
    cashier: (s) => s.cashier_name || s.cashier || "",
    location: (s) => s.location_name || "",
    closed_at: (s) => s.closed_at || "",
    sales: (s) => (s.report?.sales_count) ?? 0,
    grand: (s) => (s.report?.grand_total) ?? 0,
    cash: (s) => (s.report?.cash_total) ?? 0,
    card: (s) => (s.report?.card_total) ?? 0,
    expected: (s) => (s.report?.expected_drawer) ?? 0,
    overshort: (s) => (s.report?.over_short) ?? 0,
  };
  const shiftsPaged = usePaged(sortRows(shifts, shiftAccessors), 50);

  return (
    <div className="space-y-4 fade-in" data-testid="shifts-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <DoorClosed className="h-5 w-5 text-brand-green" />
          <div><p className="overline">Kasa</p><h1 className="font-heading text-2xl font-bold text-slate-900">Vardiyalar / Z Raporları</h1></div>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {showLocFilter && (
            <select data-testid="shifts-location-filter" value={locId} onChange={(e) => setLocId(e.target.value)}
              className="h-9 rounded-md border border-input bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-brand-green">
              <option value="">Tüm Lokasyonlar</option>
              {locOpts.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          )}
          <div className="inline-flex items-center gap-1 rounded-md bg-white p-1 ring-1 ring-inset ring-slate-200" data-testid="shifts-presets">
            {[["1d", "1 Gün"], ["3d", "3 Gün"], ["1w", "1 Hafta"], ["1m", "1 Ay"]].map(([k, l]) => (
              <button key={k} data-testid={`shift-preset-${k}`} onClick={() => setRange(shiftPreset(k))}
                className="rounded-sm px-3 py-1 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-100">{l}</button>
            ))}
          </div>
          <DatePickerTR testid="shifts-from" value={range.start} onChange={(v) => setRange((x) => ({ ...x, start: v }))} className="mt-0 h-9 w-40" />
          <span className="text-xs text-slate-400">–</span>
          <DatePickerTR testid="shifts-to" value={range.end} onChange={(v) => setRange((x) => ({ ...x, end: v }))} className="mt-0 h-9 w-40" />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="inline-flex rounded-md bg-white p-1 ring-1 ring-inset ring-slate-200" data-testid="shifts-view-tabs">
          {[["shifts", "Vardiyalar (Z)"], ["diff", "Kasiyer Kasa Farkları"], ...(canCloseAny ? [["open", "Açık Kasalar (Z Al)"]] : [])].map(([k, l]) => (
            <button key={k} data-testid={`shifts-view-${k}`} onClick={() => setView(k)}
              className={`rounded-sm px-4 py-1.5 text-sm font-semibold transition-colors ${view === k ? "bg-brand-green text-white" : "text-slate-500 hover:text-slate-800"}`}>{l}</button>
          ))}
        </div>
        {view === "diff" && (
          <Button data-testid="cash-diff-export" size="sm" variant="outline" onClick={exportCsv} disabled={exporting}
            className="border-brand-green text-brand-green hover:bg-brand-green/10">
            <Download className="mr-1.5 h-4 w-4" /> {exporting ? "İndiriliyor…" : "CSV İndir"}
          </Button>
        )}
      </div>

      {canSetThr && (
        <div className="flex flex-wrap items-center gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm ring-1 ring-inset ring-amber-200" data-testid="cash-threshold-box">
          <span className="font-medium text-amber-800">Kasa farkı uyarı eşiği:</span>
          <input data-testid="cash-threshold-input" inputMode="decimal" value={thr} onChange={(e) => setThr(e.target.value)}
            className="h-8 w-24 rounded-md border border-amber-300 bg-white px-2 text-sm tabular focus:outline-none focus:ring-2 focus:ring-amber-400" />
          <span className="text-amber-700">{curSym()}</span>
          <Button data-testid="cash-threshold-save" size="sm" onClick={saveThr} className="h-8 bg-amber-600 text-white hover:bg-amber-700">Kaydet</Button>
          <label className="flex items-center gap-1.5 text-xs font-medium text-amber-800">
            <input type="checkbox" data-testid="cash-threshold-email" checked={emailThr} onChange={(e) => setEmailThr(e.target.checked)} className="h-4 w-4 rounded border-amber-300" />
            E-posta ile de bildir
          </label>
          <span className="text-xs text-amber-700">Bu tutarı aşan açık/fazla olduğunda ilgili müdüre anlık uygulama-içi bildirim gider{emailThr ? " + e-posta" : ""}.</span>
        </div>
      )}

      <div className="overflow-x-auto rounded-md bg-white ring-1 ring-inset ring-slate-200">
        {view === "diff" ? <CashDiffReport range={range} locId={locId} /> : view === "open" ? (
        <table className="w-full text-sm" data-testid="open-shifts-table">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Kasiyer</th>
              <th className="px-4 py-3 font-medium">Lokasyon</th>
              <th className="px-4 py-3 font-medium">Açılış</th>
              <th className="px-4 py-3 text-right font-medium">Satış</th>
              <th className="px-4 py-3 text-right font-medium">Ciro</th>
              <th className="px-4 py-3 text-right font-medium">Kasada Olması Gereken</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {openLoading && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Yükleniyor…</td></tr>}
            {!openLoading && openShifts.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Şu an açık kasa yok.</td></tr>}
            {openShifts.map((s) => (
              <tr key={s.id} data-testid={`open-shift-row-${s.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-700">{s.cashier_name}</td>
                <td className="px-4 py-3 text-slate-500">{s.location_name || "—"}</td>
                <td className="px-4 py-3 text-slate-500">{dt(s.opened_at)}</td>
                <td className="px-4 py-3 text-right tabular text-slate-600">{s.sales_count ?? 0}</td>
                <td className="px-4 py-3 text-right tabular font-bold text-brand-green">{fmtTL(s.grand_total ?? 0)}</td>
                <td className="px-4 py-3 text-right tabular font-semibold text-slate-800">{fmtTL(s.expected_drawer ?? 0)}</td>
                <td className="px-4 py-3 text-right">
                  {s.has_open_cart ? (
                    <span className="text-[11px] font-medium text-amber-600">Açık sepet var</span>
                  ) : (
                    <button data-testid={`open-shift-close-${s.id}`} onClick={() => { setCloseTarget(s); setCounted("0"); setAdminPin(""); }}
                      className="inline-flex items-center gap-1 rounded-md bg-brand-green px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-brand-green-600">
                      <DoorClosed className="h-3.5 w-3.5" /> Z Raporu Al & Kapat
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr>
              <SortTh label="Kasiyer" k="cashier" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="shift-sort-cashier" />
              <SortTh label="Lokasyon" k="location" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="shift-sort-location" />
              <SortTh label="Kapanış" k="closed_at" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="shift-sort-closed" />
              <SortTh label="Satış" k="sales" align="right" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="shift-sort-sales" />
              <SortTh label="Ciro" k="grand" align="right" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="shift-sort-grand" />
              <SortTh label="Nakit" k="cash" align="right" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="shift-sort-cash" />
              <SortTh label="Kart" k="card" align="right" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="shift-sort-card" />
              <SortTh label="Kasada Olması Gereken" k="expected" align="right" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="shift-sort-expected" />
              <SortTh label="Açık/Fazla" k="overshort" align="right" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="shift-sort-overshort" />
              <th className="px-4 py-3 font-medium">Kapatan</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={11} className="px-4 py-8 text-center text-slate-400">Yükleniyor...</td></tr>}
            {!loading && shifts.length === 0 && <tr><td colSpan={11} className="px-4 py-10 text-center text-slate-400">Kapatılmış vardiya yok.</td></tr>}
            {sortRows(shifts, shiftAccessors).length > 0 && shiftsPaged.slice.map((s) => {
              const rep = s.report || {};
              return (
                <tr key={s.id} data-testid={`shift-row-${s.id}`} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-700">{s.cashier_name || s.cashier}</td>
                  <td className="px-4 py-3 text-slate-500">{s.location_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{dt(s.closed_at)}</td>
                  <td className="px-4 py-3 text-right tabular text-slate-600">{rep.sales_count ?? 0}</td>
                  <td className="px-4 py-3 text-right tabular font-bold text-brand-green">{fmtTL(rep.grand_total ?? 0)}</td>
                  <td className="px-4 py-3 text-right tabular text-emerald-700">{fmtTL(rep.cash_total ?? 0)}</td>
                  <td className="px-4 py-3 text-right tabular text-sky-700">{fmtTL(rep.card_total ?? 0)}</td>
                  <td className="px-4 py-3 text-right tabular font-semibold text-slate-800">{fmtTL(rep.expected_drawer ?? 0)}</td>
                  <td className="px-4 py-3 text-right tabular font-semibold">{rep.over_short == null ? <span className="text-slate-300">—</span> : <span data-testid={`shift-overshort-${s.id}`} className={rep.over_short < 0 ? "text-red-600" : rep.over_short > 0 ? "text-emerald-700" : "text-slate-400"}>{rep.over_short < 0 ? "−" : rep.over_short > 0 ? "+" : ""}{fmtTL(Math.abs(rep.over_short))}</span>}</td>
                  <td className="px-4 py-3">
                    {s.closed_self ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600"><User className="h-3 w-3" /> Kendisi</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200" title={s.closed_approved_by}><ShieldCheck className="h-3 w-3" /> Amir onaylı</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`shift-detail-${s.id}`} onClick={() => openDetail(s)}
                      className="inline-flex items-center gap-1 rounded-md border border-brand-green px-2 py-1 text-[11px] font-semibold text-brand-green hover:bg-brand-green/10">
                      <Eye className="h-3.5 w-3.5" /> Z Raporu
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        )}
      </div>

      {view === "shifts" && !loading && shifts.length > 0 && (
        <div className="mt-1"><Pagination page={shiftsPaged.page} pageSize={shiftsPaged.pageSize} total={shiftsPaged.total}
          onPage={shiftsPaged.setPage} onPageSize={shiftsPaged.setPageSize} testid="shifts-pagination" /></div>
      )}

      <Dialog open={!!closeTarget} onOpenChange={(o) => !o && setCloseTarget(null)}>
        <DialogContent data-testid="admin-close-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Kasayı Kapat — Z Raporu Al</DialogTitle>
            <DialogDescription>{closeTarget?.cashier_name} · {closeTarget?.location_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
              Kasada olması gereken: <b className="tabular">{fmtTL(closeTarget?.expected_drawer ?? 0)}</b>
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Sayılan Nakit ({curSym()})</label>
              <input data-testid="admin-close-counted" inputMode="decimal" value={counted} onChange={(e) => setCounted(e.target.value)}
                className="h-11 w-full rounded-md border border-input bg-white px-3 text-sm tabular focus:outline-none focus:ring-2 focus:ring-brand-green" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">PIN Kodunuz</label>
              <input data-testid="admin-close-pin" type="password" inputMode="numeric" value={adminPin} onChange={(e) => setAdminPin(e.target.value.replace(/\D/g, ""))}
                placeholder="Yönetici PIN'iniz" className="h-11 w-full rounded-md border border-input bg-white px-3 text-sm tracking-widest focus:outline-none focus:ring-2 focus:ring-brand-green" />
              <p className="mt-1 text-[11px] text-slate-400">Kimliğinizi doğrulamak için kendi PIN'inizi girin. Big BOSS PIN'siz de kapatabilir.</p>
            </div>
            <Button data-testid="admin-close-submit" onClick={submitAdminClose} disabled={closing} className="w-full bg-brand-green text-white hover:bg-brand-green-600">
              {closing ? "Kapatılıyor…" : "Z Raporunu Kaydet & Kasayı Kapat"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="shift-detail-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Z Raporu — {detail?.shift?.cashier_name || detail?.shift?.cashier}</DialogTitle>
            <DialogDescription>
              {detail?.shift?.location_name} · {tm(detail?.shift?.opened_at)} → {tm(detail?.shift?.closed_at)}
              {detail?.shift && (detail.shift.closed_self ? " · kasiyer kendi kapattı" : ` · amir onayı: ${detail.shift.closed_approved_by || ""}`)}
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3">
              <div className="rounded-md bg-slate-50 px-3 py-2 ring-1 ring-inset ring-slate-200">
                <Row label="Açılış (devir)" value={fmtTL(r.opening_amount)} />
                <Row label="Satış Adedi" value={r.sales_count ?? 0} />
                <Row label="Toplam Satış" value={fmtTL(r.grand_total)} />
                <Row label="Nakit" value={fmtTL(r.cash_total)} accent="text-emerald-700" />
                <Row label="Kredi Kartı" value={fmtTL(r.card_total)} accent="text-sky-700" />
                {r.qr_total > 0 && <Row label="QR (Banka/Mobil)" value={fmtTL(r.qr_total)} accent="text-indigo-700" />}
                <Row label="Verilen İndirim" value={fmtTL(r.discount_total)} accent="text-red-600" />
                <Row label="Kasada Olması Gereken" value={fmtTL(r.expected_drawer)} strong accent="text-brand-green" />
                {r.counted_cash != null && <Row label="Sayılan Nakit" value={fmtTL(r.counted_cash)} />}
                {r.over_short != null && <Row label={r.over_short < 0 ? "Kasa Açığı" : r.over_short > 0 ? "Kasa Fazlası" : "Kasa Farkı"} value={fmtTL(Math.abs(r.over_short))} strong accent={r.over_short < 0 ? "text-red-600" : r.over_short > 0 ? "text-emerald-700" : "text-slate-500"} />}
              </div>
              {r.meal_surcharge_total > 0 && (
                <div className="rounded-md bg-purple-50 px-3 py-2 ring-1 ring-inset ring-purple-200" data-testid="shift-meal-breakdown">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-purple-700">Yemek Kartı Komisyon Farkı</p>
                  {(r.meal_by_provider || []).map((m, i) => (
                    <div key={i} data-testid={`shift-meal-prov-${i}`} className="flex items-center justify-between py-0.5 text-sm">
                      <span className="text-slate-600">{m.provider} <span className="text-[11px] text-slate-400">({m.count} işlem)</span></span>
                      <span className="tabular font-semibold text-purple-700">{fmtTL(m.surcharge)}</span>
                    </div>
                  ))}
                  <div className="mt-1 flex items-center justify-between border-t border-purple-200 pt-1 text-sm font-bold">
                    <span className="text-slate-700">Toplam komisyon farkı</span>
                    <span className="tabular text-purple-700" data-testid="shift-meal-total">{fmtTL(r.meal_surcharge_total)}</span>
                  </div>
                </div>
              )}
              <div>
                <p className="overline mb-1">Vardiya Satışları</p>
                <div className="max-h-64 overflow-y-auto rounded-md ring-1 ring-inset ring-slate-200">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-slate-50 text-left text-xs text-slate-500">
                      <tr><th className="px-3 py-2 font-medium">Fiş No</th><th className="px-3 py-2 font-medium">Saat</th><th className="px-3 py-2 font-medium">Müşteri</th><th className="px-3 py-2 text-right font-medium">Tutar</th></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {detail.sales === null && <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">Yükleniyor...</td></tr>}
                      {detail.sales !== null && detail.sales.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">Bu vardiyada satış yok.</td></tr>}
                      {(detail.sales || []).map((s) => (
                        <tr key={s.id} data-testid={`shift-sale-${s.id}`}>
                          <td className="px-3 py-2 font-mono text-xs text-slate-600">{s.sale_no}</td>
                          <td className="px-3 py-2 text-slate-500">{tm(s.created_at)}</td>
                          <td className="px-3 py-2 text-slate-600">{s.customer_name || "—"}</td>
                          <td className="px-3 py-2 text-right tabular font-semibold text-brand-green">{fmtTL(s.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function CashDiffReport({ range, locId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const paged = usePaged(rows, 50);
  useEffect(() => {
    setLoading(true);
    const params = {};
    if (range.start) params.start = `${range.start}T00:00:00`;
    if (range.end) params.end = `${range.end}T23:59:59.999999`;
    if (locId) params.location_id = locId;
    api.get("/shifts/cash-diff-report", { params }).then((r) => setRows(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [range, locId]);
  const dt = (s) => (s ? String(s).slice(0, 10) : "—");
  const sign = (n) => (n < 0 ? "−" : n > 0 ? "+" : "");
  const cls = (n) => (n < 0 ? "text-red-600" : n > 0 ? "text-emerald-700" : "text-slate-400");
  return (
    <>
    <table className="w-full text-sm" data-testid="cash-diff-table">
      <thead className="bg-slate-50 text-left text-xs text-slate-500">
        <tr>
          <th className="px-4 py-3 font-medium">Kasiyer</th>
          <th className="px-4 py-3 text-right font-medium">Sayım</th>
          <th className="px-4 py-3 text-right font-medium">Açık / Fazla</th>
          <th className="px-4 py-3 text-right font-medium">Toplam Açık</th>
          <th className="px-4 py-3 text-right font-medium">Toplam Fazla</th>
          <th className="px-4 py-3 text-right font-medium">Net</th>
          <th className="px-4 py-3 text-right font-medium">En Büyük Fark</th>
          <th className="px-4 py-3 font-medium">Son</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {loading && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Yükleniyor...</td></tr>}
        {!loading && rows.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">Sayım yapılmış vardiya yok. Kasa kapanışında "Sayılan Nakit" girildikçe burada birikir.</td></tr>}
        {rows.length > 0 && paged.slice.map((r) => {
          const risk = r.short_count >= 3 && r.net < 0;
          return (
            <tr key={r.cashier} data-testid={`cash-diff-${r.cashier}`} className={`hover:bg-slate-50 ${risk ? "bg-red-50/60" : ""}`}>
              <td className="px-4 py-3 font-medium text-slate-700">
                {r.cashier_name}
                {risk && <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700">sık açık</span>}
                {r.locations?.length > 0 && <span className="ml-1 block text-[10px] text-slate-400">{r.locations.join(", ")}</span>}
              </td>
              <td className="px-4 py-3 text-right tabular text-slate-600">{r.count}</td>
              <td className="px-4 py-3 text-right tabular"><span className="text-red-600">{r.short_count}</span> / <span className="text-emerald-700">{r.over_count}</span></td>
              <td className="px-4 py-3 text-right tabular font-semibold text-red-600">{r.total_short > 0 ? fmtTL(r.total_short) : "—"}</td>
              <td className="px-4 py-3 text-right tabular font-semibold text-emerald-700">{r.total_over > 0 ? fmtTL(r.total_over) : "—"}</td>
              <td className={`px-4 py-3 text-right tabular font-bold ${cls(r.net)}`}>{sign(r.net)}{fmtTL(Math.abs(r.net))}</td>
              <td className={`px-4 py-3 text-right tabular ${cls(r.worst)}`}>{sign(r.worst)}{fmtTL(Math.abs(r.worst))}</td>
              <td className="px-4 py-3 text-xs text-slate-400">{dt(r.last_at)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
    {!loading && rows.length > 0 && (
      <div className="border-t border-slate-100 p-1"><Pagination page={paged.page} pageSize={paged.pageSize} total={paged.total}
        onPage={paged.setPage} onPageSize={paged.setPageSize} testid="cash-diff-pagination" /></div>
    )}
    </>
  );
}
